/* ─────────────────────────────────────────────────────────────
   ChoferIntervencion.jsx — DMU 614 / Sprint Camellones (d)

   Flujo:
     1) Captura GPS automático → POST /api/intervenciones/detectar
     2) Renderiza <MapaSubpuntos> con datos del backend
     3) Selección manual o sugerida → "Confirmar"
     4) Pantalla de vuelta: fotos + litros + tipo descarga
     5) Cerrar intervención con cálculo de cobertura
   ───────────────────────────────────────────────────────────── */

import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import MapaSubpuntos from "../components/MapaSubpuntos";
import PhotoPicker from "../components/PhotoPicker";
import { useAuth } from "../auth/AuthContext";
import { useGPS, ChoferLayout } from "./chofer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ═══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
   ═══════════════════════════════════════════════════════════ */
export default function ChoferIntervencion() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { coords, capture } = useGPS();
  const [vista, setVista] = useState("lista"); // lista | gps (gps = flujo de detección automática)
  const [gpsPrecision, setGpsPrecision] = useState(null);
  const [det, setDet] = useState(null);          // respuesta /detectar
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seleccionado, setSeleccionado] = useState(null);
  const [metodo, setMetodo] = useState("GPS_AUTO");
  const [intervencion, setIntervencion] = useState(null);
  const [vueltas, setVueltas] = useState([]);
  const [pantalla, setPantalla] = useState("mapa"); // mapa | vuelta | cerrar
  const detectRanRef = useRef(false);

  // ── Detección inicial ───────────────────────────────────
  const detectar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await capture();
      setGpsPrecision(c.acc);
      const r = await axios.post(`${API}/intervenciones/detectar`, {
        gps_lat: c.lat,
        gps_lon: c.lon,
        gps_precision_m: c.acc,
      });
      setDet(r.data);
      if (r.data.intervencion_abierta) {
        setIntervencion(r.data.intervencion_abierta);
        const vr = await axios.get(`${API}/intervenciones/${r.data.intervencion_abierta.id}`);
        setVueltas(vr.data.vueltas || []);
      }
      if (r.data.subpunto_sugerido_id) {
        // Sugerida amarilla, NO seleccionada hasta que el chofer la toque
        setSeleccionado(null);
        setMetodo("GPS_AUTO");
      } else {
        setSeleccionado(null);
        setMetodo("TOCK_MANUAL");
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [capture]);

  useEffect(() => {
    if (vista !== "gps") return;
    if (detectRanRef.current) return;
    detectRanRef.current = true;
    detectar();
  }, [vista, detectar]);

  // ── VISTA POR DEFECTO: lista de parques del día con 2 botones ──
  if (vista === "lista") {
    return (
      <ListaParquesIntervencion
        onUsarGPS={() => { detectRanRef.current = false; setVista("gps"); }}
        onSalir={() => navigate("/chofer")}
      />
    );
  }

  // ── Estado visual por subpunto (REGADA si tuvo vuelta del día) ──
  const estadosHoy = (det?.subpuntos || []).reduce((acc, sp) => {
    if (sp.estado_hoy) acc[sp.id] = sp.estado_hoy;
    return acc;
  }, {});

  // ── Acciones ────────────────────────────────────────────
  const handleSubpuntoTap = (sp_id) => {
    setSeleccionado(sp_id);
    // si el chofer tocó el sugerido, mantenemos GPS_AUTO; si no, TOCK_MANUAL
    if (sp_id === det?.subpunto_sugerido_id) setMetodo("GPS_AUTO");
    else setMetodo("TOCK_MANUAL");
  };

  const corregirManual = () => {
    setMetodo("GPS_CORREGIDO_MANUAL");
    toast.info("Modo manual activado. El método se registrará para auditoría.");
  };

  const confirmar = async () => {
    if (!seleccionado && det?.tiene_subpuntos) {
      toast.error("Selecciona un sub-punto antes de confirmar");
      return;
    }
    try {
      // Crear intervención si no había abierta
      let inter = intervencion;
      if (!inter) {
        const r = await axios.post(`${API}/intervenciones`, {
          espacio_id: det.espacio.id,
          gps_lat: coords?.lat,
          gps_lon: coords?.lon,
          tipo_servicio: det.espacio.tipo_servicio || "RIEGO_AREA_VERDE",
        });
        inter = r.data;
        setIntervencion(inter);
      }
      setPantalla("vuelta");
    } catch (e) {
      toast.error("Error creando intervención: " + (e.response?.data?.detail || e.message));
    }
  };

  // ── Render ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="chofer-page">
        <ChoferLayout title="Detectando ubicación" back="/chofer" />
        <div className="chofer-body" data-testid="interv-loading">
          <div className="card-base text-center p-8">
            <i className="fa-solid fa-location-crosshairs text-3xl text-sky-500 mb-3 fa-beat" />
            <div>Capturando GPS y buscando el espacio más cercano…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chofer-page">
        <ChoferLayout title="Error" back="/chofer" />
        <div className="chofer-body">
          <div className="card-base text-center p-8" data-testid="interv-error">
            <i className="fa-solid fa-triangle-exclamation text-3xl text-red-500 mb-3" />
            <div className="font-semibold">{error}</div>
            <button className="btn-primary mt-4" onClick={detectar} data-testid="btn-reintentar">Reintentar</button>
            <button className="btn-ghost w-full mt-2" onClick={() => { detectRanRef.current = false; setVista("lista"); setError(null); }}>← Ver mis parques</button>
          </div>
        </div>
      </div>
    );
  }

  if (!det || det.tipo === "SIN_ESPACIO") {
    return (
      <div className="chofer-page">
        <ChoferLayout title="Sin espacio cercano" back="/chofer" />
        <div className="chofer-body">
          <div className="card-base text-center p-8" data-testid="interv-sin-espacio">
            <i className="fa-solid fa-map-location-dot text-3xl text-slate-400 mb-3" />
            <div className="font-semibold">No hay espacio asignado en tu ubicación</div>
            <div className="text-sm text-slate-500 mt-2">
              GPS: {coords?.lat?.toFixed(6)}, {coords?.lon?.toFixed(6)}<br/>
              Precisión: ±{Math.round(gpsPrecision || 0)} m
            </div>
            <button className="btn-primary mt-6 w-full" data-testid="btn-ver-lista" onClick={() => { detectRanRef.current = false; setVista("lista"); }}>
              <i className="fa-solid fa-list-ul mr-1"/> Ver mis parques del día
            </button>
            <button className="btn-ghost w-full mt-2" onClick={detectar}>Volver a detectar por GPS</button>
            <button className="btn-ghost w-full mt-2" onClick={() => navigate("/chofer")}>Salir</button>
          </div>
        </div>
      </div>
    );
  }

  // Pantalla "vuelta": registrar la vuelta seleccionada
  if (pantalla === "vuelta") {
    return (
      <PantallaVuelta
        intervencion={intervencion}
        det={det}
        coords={coords}
        seleccionado={seleccionado}
        metodo={metodo}
        onVueltaCreada={async (nuevaVuelta) => {
          setVueltas([...vueltas, nuevaVuelta]);
          // Refrescar /detectar para actualizar estado_hoy
          const r = await axios.post(`${API}/intervenciones/detectar`, {
            gps_lat: coords?.lat,
            gps_lon: coords?.lon,
          });
          setDet(r.data);
          setSeleccionado(null);
          setPantalla("mapa");
          toast.success(`Vuelta ${nuevaVuelta.numero_vuelta} guardada`);
        }}
        onCerrar={() => setPantalla("cerrar")}
        onCancelar={() => setPantalla("mapa")}
      />
    );
  }

  if (pantalla === "cerrar") {
    return (
      <PantallaCerrar
        intervencion={intervencion}
        det={det}
        vueltas={vueltas}
        onCerrado={() => navigate("/chofer")}
        onCancelar={() => setPantalla("mapa")}
      />
    );
  }

  // Pantalla principal: mapa de sub-puntos
  const espacio = det.espacio;
  const hayIntervAbierta = !!intervencion;
  const subSel = (det.subpuntos || []).find((s) => s.id === seleccionado);
  const gpsFueraRadio = det.tiene_subpuntos && !det.subpunto_sugerido_id;

  return (
    <div className="chofer-page bg-slate-50" data-testid="page-chofer-intervencion">
      <ChoferLayout title={espacio.nombre} back="/chofer" />
      <div className="chofer-body" style={{ paddingBottom: 120 }}>
        {/* Cabecera info */}
        <div className="card-base p-3 mb-3" data-testid="info-espacio">
          <div className="flex items-center gap-2 text-sm">
            <span className="pill-tiny pill-zone">{espacio.zona_id}</span>
            <span className="pill-tiny pill-water">{espacio.tipo_agua}</span>
            <span className="pill-tiny pill-baja">{espacio.tipo_layout_visual}</span>
            <span className="text-slate-500 ml-auto">📍 {det.distancia_gps_m}m</span>
          </div>
          {det.pipa_chofer?.es_cobertura && (
            <div className="text-xs text-amber-700 mt-1" data-testid="badge-cobertura">
              <i className="fa-solid fa-hand-holding-droplet" /> Operando pipa <strong>{det.pipa_chofer.numero}</strong> por cobertura
            </div>
          )}
        </div>

        {/* Modal intervención abierta */}
        {hayIntervAbierta && (
          <div className="card-base p-4 mb-3 border-l-4 border-amber-500" data-testid="modal-abierta">
            <div className="font-bold text-amber-900">
              <i className="fa-solid fa-clock-rotate-left mr-1" /> Intervención abierta desde {new Date(intervencion.fecha_inicio_iso).toLocaleTimeString("es-MX", {hour: "2-digit", minute: "2-digit"})}
            </div>
            {vueltas.length > 0 && (
              <ul className="text-sm mt-2 space-y-1" data-testid="lista-vueltas">
                {vueltas.map((v) => (
                  <li key={v.id} className="flex gap-2">
                    <i className="fa-solid fa-check text-emerald-600" />
                    <span>Vuelta {v.numero_vuelta} — {v.metodo_seleccion} · {v.litros}L</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Aviso GPS fuera de radio */}
        {gpsFueraRadio && (
          <div className="card-base p-3 mb-3 bg-amber-50 border border-amber-200" data-testid="aviso-gps-fuera">
            <div className="text-sm text-amber-900">
              <i className="fa-solid fa-circle-info" /> El GPS no determinó la rama exacta — toca dónde estás regando.
            </div>
          </div>
        )}

        {/* Mapa */}
        <MapaSubpuntos
          tipo_layout={det.tipo_layout_visual}
          subpuntos={det.subpuntos || []}
          subpunto_sugerido_id={det.subpunto_sugerido_id}
          subpunto_seleccionado_id={seleccionado}
          estados_hoy={estadosHoy}
          gps_actual={coords ? { lat: coords.lat, lon: coords.lon } : null}
          onSubpuntoTap={handleSubpuntoTap}
          cobertura_pct={det.cobertura_actual_pct || 0}
        />

        {/* Si el espacio NO tiene subpuntos (camellón lineal sin tramos GPS) */}
        {!det.tiene_subpuntos && (
          <div className="card-base p-4 mt-3 text-center" data-testid="info-sin-subpuntos">
            <i className="fa-solid fa-road text-2xl text-slate-400" />
            <div className="mt-2 text-sm">Este espacio se registra como un tramo único.</div>
          </div>
        )}
      </div>

      {/* Sticky footer con botones */}
      <div className="interv-footer" data-testid="interv-footer">
        {det.tiene_subpuntos && (
          <button
            className="btn-ghost-sm"
            onClick={corregirManual}
            data-testid="btn-gps-malo"
          >
            <i className="fa-solid fa-pen" /> El GPS está mal
          </button>
        )}
        <button
          className="btn-primary flex-1"
          disabled={det.tiene_subpuntos && !seleccionado}
          onClick={confirmar}
          data-testid="btn-confirmar"
        >
          {det.tiene_subpuntos && subSel
            ? `Confirmar: estoy en ${subSel.codigo}`
            : det.tiene_subpuntos
            ? "Selecciona un sub-punto"
            : "Confirmar y registrar vuelta"}
        </button>
        {hayIntervAbierta && (
          <button className="btn-ghost-sm" onClick={() => setPantalla("cerrar")} data-testid="btn-cerrar-intervencion">
            <i className="fa-solid fa-flag-checkered" /> Cerrar
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PantallaVuelta — Registrar foto, litros, tipo descarga
   ═══════════════════════════════════════════════════════════ */
function PantallaVuelta({ intervencion, det, coords, seleccionado, metodo, onVueltaCreada, onCerrar, onCancelar }) {
  const [litros, setLitros] = useState(10000);
  const [tipo, setTipo] = useState("RIEGO_DIRECTO");
  const [observ, setObserv] = useState("");
  const [fotos, setFotos] = useState([]);
  const [enviando, setEnviando] = useState(false);

  const subpuntoSel = (det.subpuntos || []).find((s) => s.id === seleccionado);

  const guardar = async (salir) => {
    if (fotos.length < 1) {
      toast.error("Sube al menos 1 foto");
      return;
    }
    setEnviando(true);
    try {
      // Crear vuelta
      const r = await axios.post(`${API}/intervenciones/${intervencion.id}/vueltas`, {
        gps_lat: coords?.lat,
        gps_lon: coords?.lon,
        subpunto_id: seleccionado,
        metodo_seleccion: metodo,
        litros_cargados: litros,
        tipo_descarga: tipo,
        observaciones: observ || null,
        hora_inicio_iso: new Date().toISOString(),
      });
      const vuelta = r.data;
      // Subir fotos comprimidas (base64)  — TODO Fase 1C: migrar a upload directo Cloudinary con signed URL
      for (const f of fotos) {
        await axios.post(`${API}/intervenciones/${intervencion.id}/vueltas/${vuelta.id}/fotos`, {
          base64: f.dataURL,
          filename: f.filename || "vuelta.jpg",
          momento: "DURANTE",
          gps_lat: coords?.lat,
          gps_lon: coords?.lon,
        });
      }
      onVueltaCreada(vuelta);
      if (salir === "recargar") {
        toast.success("Vuelta guardada — sal a recargar");
      } else if (salir === "cerrar") {
        onCerrar();
      }
    } catch (e) {
      if (!navigator.onLine) {
        toast.error("Sin conexión — reintenta en un momento");
        localStorage.setItem("dmu_vuelta_pending_" + Date.now(), JSON.stringify({ intervencion_id: intervencion.id, payload: { litros, tipo, fotos: fotos.length } }));
        // TODO Fase 1D — implementar cola offline robusta con sync automático
      } else {
        toast.error("Error: " + (e.response?.data?.detail || e.message));
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="chofer-page bg-slate-50" data-testid="page-vuelta">
      <ChoferLayout title={`Vuelta en ${subpuntoSel?.codigo || "tramo"}`} back={null} />
      <div className="chofer-body" style={{ paddingBottom: 140 }}>
        <div className="card-base p-3 mb-3">
          <div className="text-sm text-slate-500">Método de selección</div>
          <div className="font-semibold" data-testid="metodo-seleccion">{metodo}</div>
        </div>

        <div className="card-base p-3 mb-3">
          <label className="text-sm font-semibold">Litros cargados</label>
          <input
            type="number"
            value={litros}
            onChange={(e) => setLitros(parseInt(e.target.value || 0))}
            className="w-full mt-2 p-2 border rounded"
            data-testid="input-litros"
          />
        </div>

        <div className="card-base p-3 mb-3">
          <label className="text-sm font-semibold">Tipo de descarga</label>
          <select className="w-full mt-2 p-2 border rounded" value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="select-tipo">
            <option value="RIEGO_DIRECTO">Riego directo</option>
            <option value="LLENADO_TINACO">Llenado de tinaco</option>
            <option value="OTRO">Otro</option>
          </select>
        </div>

        <div className="card-base p-3 mb-3">
          <label className="text-sm font-semibold">Fotos (mín 1, máx 6)</label>
          <div className="mt-2">
            <PhotoPicker fotos={fotos} setFotos={setFotos} max={6} />
          </div>
        </div>

        <div className="card-base p-3 mb-3">
          <label className="text-sm font-semibold">Observaciones (opcional)</label>
          <textarea
            value={observ}
            onChange={(e) => setObserv(e.target.value)}
            className="w-full mt-2 p-2 border rounded"
            rows={2}
            data-testid="input-observ"
          />
        </div>
      </div>

      <div className="interv-footer flex-col gap-2">
        <button className="btn-primary w-full" disabled={enviando} onClick={() => guardar("recargar")} data-testid="btn-guardar-recargar">
          Guardar y salir a recargar
        </button>
        <button className="btn-ghost w-full" disabled={enviando} onClick={() => guardar("mover")} data-testid="btn-guardar-mover">
          Guardar y mover a otra rama
        </button>
        <button className="btn-ghost-sm w-full text-red-600" disabled={enviando} onClick={onCancelar} data-testid="btn-cancelar-vuelta">
          Cancelar
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PantallaCerrar — Cerrar intervención con cálculo de cobertura
   ═══════════════════════════════════════════════════════════ */
function PantallaCerrar({ intervencion, det, vueltas, onCerrado, onCancelar }) {
  const total = det.subpuntos?.length || 0;
  const atendidos = new Set(vueltas.map((v) => v.subpunto_id).filter(Boolean)).size;
  const cobertura = total ? Math.round((atendidos / total) * 100) : (vueltas.length ? 100 : 0);
  const sugiereParcial = total && cobertura < 100;
  const [estado, setEstado] = useState(sugiereParcial ? "PARCIAL" : "COMPLETADA");
  const [obs, setObs] = useState("");
  const [enviando, setEnviando] = useState(false);

  const faltantes = (det.subpuntos || []).filter(
    (s) => !vueltas.some((v) => v.subpunto_id === s.id)
  );

  const cerrar = async () => {
    setEnviando(true);
    try {
      await axios.put(`${API}/intervenciones/${intervencion.id}/cerrar`, {
        estado,
        observaciones: obs || null,
        forzar: estado === "COMPLETADA" && cobertura < 100,
      });
      toast.success(`Intervención cerrada — ${cobertura}% cobertura`);
      onCerrado();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="chofer-page bg-slate-50" data-testid="page-cerrar">
      <ChoferLayout title="Cerrar intervención" back={null} />
      <div className="chofer-body">
        <div className="card-base p-4 mb-3 text-center">
          <div className="text-5xl font-bold" data-testid="cobertura-final">{cobertura}%</div>
          <div className="text-sm text-slate-500">cobertura · {atendidos} de {total || vueltas.length}</div>
        </div>

        {sugiereParcial && (
          <div className="card-base p-3 mb-3 bg-amber-50 border border-amber-200" data-testid="sugerencia-parcial">
            <div className="font-semibold text-amber-900">Falta cobertura</div>
            <ul className="text-sm mt-1 list-disc list-inside text-amber-800">
              {faltantes.map((s) => <li key={s.id}>{s.codigo}</li>)}
            </ul>
          </div>
        )}

        <div className="card-base p-3 mb-3">
          <label className="text-sm font-semibold">Estado final</label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {["COMPLETADA", "PARCIAL", "CANCELADA"].map((e) => (
              <button
                key={e}
                className={`p-2 border rounded ${estado === e ? "bg-slate-900 text-white" : "bg-white"}`}
                onClick={() => setEstado(e)}
                data-testid={`btn-estado-${e}`}
              >{e}</button>
            ))}
          </div>
        </div>

        <div className="card-base p-3 mb-3">
          <label className="text-sm font-semibold">Observaciones</label>
          <textarea value={obs} onChange={(e) => setObs(e.target.value)} className="w-full mt-2 p-2 border rounded" rows={3} data-testid="input-obs-cierre" />
        </div>
      </div>

      <div className="interv-footer flex-col gap-2">
        <button className="btn-primary w-full" onClick={cerrar} disabled={enviando} data-testid="btn-confirmar-cierre">
          Cerrar como {estado}
        </button>
        <button className="btn-ghost-sm w-full" onClick={onCancelar} disabled={enviando} data-testid="btn-cancelar-cierre">
          Volver al mapa
        </button>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   ListaParquesIntervencion
   Vista por defecto: el chofer ve la lista completa de sus
   parques del día y elige cualquiera (no tiene que seguir
   secuencia). Cada tarjeta: "Ver en mapa" + "Validar punto".
   Mantiene la opción de "Detectar por GPS" como secundaria.
   ═══════════════════════════════════════════════════════════ */
function ListaParquesIntervencion({ onUsarGPS, onSalir }) {
  const navigate = useNavigate();
  const [ruta, setRuta] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/chofer/ruta-hoy`)
      .then((r) => setRuta(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  if (err) return (
    <div className="chofer-page">
      <ChoferLayout title="Estoy en un punto" back="/chofer" />
      <div className="chofer-body">
        <div className="card-base text-center p-8 text-red-600" data-testid="interv-lista-error">{err}</div>
      </div>
    </div>
  );
  if (!ruta) return (
    <div className="chofer-page">
      <ChoferLayout title="Estoy en un punto" back="/chofer" />
      <div className="chofer-body" data-testid="interv-lista-loading">
        <div className="card-base text-center p-8 text-slate-500">Cargando mi ruta…</div>
      </div>
    </div>
  );

  const puntos = ruta.puntos || [];
  const pendientes = puntos.filter((p) => p.estado === "PENDIENTE").length;

  return (
    <div className="chofer-page bg-slate-50" data-testid="page-interv-lista">
      <ChoferLayout title="Estoy en un punto" back="/chofer" />
      <div className="chofer-body" style={{ paddingBottom: 100 }}>
        <div className="card-base p-3 mb-3" data-testid="interv-lista-info">
          <div className="text-sm" style={{ color: "#475569" }}>
            <i className="fa-solid fa-list-ul mr-1 text-sky-600"/>
            Toca cualquier parque de tu ruta de hoy para validarlo. No es necesario seguir la secuencia.
          </div>
          <div className="text-xs mt-1" style={{ color: "#64748B" }}>
            {puntos.length} parques · {pendientes} pendientes · {ruta.dia?.toLowerCase()}
          </div>
        </div>

        {puntos.length === 0 && (
          <div className="card-base text-center text-slate-500 py-10" data-testid="interv-lista-empty">
            <i className="fa-solid fa-calendar-xmark text-3xl mb-3 text-slate-300" />
            <div>No tienes parques asignados para hoy.</div>
          </div>
        )}

        <ul className="ruta-list" data-testid="interv-lista-ul">
          {puntos.map((p, i) => {
            const hayCoords = p.gps_lat && p.gps_lon;
            return (
              <li key={p.id || p.clave_ruta}
                  className={`ruta-item ${p.estado !== "PENDIENTE" ? "done" : ""}`}
                  data-testid={`interv-pt-${p.clave_ruta}`}>
                <div className="ruta-num">{i + 1}</div>
                <div className="ruta-info">
                  <div className="ruta-name">{p.nombre}</div>
                  <div className="ruta-id mono">{p.clave_ruta}</div>
                  {(p.ubicacion || p.colonia) && (
                    <div className="ruta-direccion">
                      <i className="fa-solid fa-location-dot text-slate-400"/>{" "}
                      {p.ubicacion || p.colonia}
                      {p.colonia && p.ubicacion ? ` · ${p.colonia}` : ""}
                    </div>
                  )}
                  <div className="ruta-pills">
                    <span className="pill-tiny pill-baja">{p.categoria || "PARQUE"}</span>
                    <span className="pill-tiny pill-zone">{p.zona_id}</span>
                    {p.estado === "ATENDIDO" && <span className="pill-tiny pill-ok"><i className="fa-solid fa-check"/> Atendido</span>}
                    {p.estado === "NO_LOCALIZADO" && <span className="pill-tiny pill-na">No localizado</span>}
                  </div>
                  <div className="ruta-actions">
                    {hayCoords ? (
                      <a className="link-map" target="_blank" rel="noreferrer"
                         href={`https://maps.google.com/?q=${p.gps_lat},${p.gps_lon}`}
                         data-testid={`interv-mapa-${p.clave_ruta}`}>
                        <i className="fa-solid fa-map-location-dot"/> Ver en mapa
                      </a>
                    ) : (
                      <span className="link-map link-map-off" aria-disabled="true"
                            data-testid={`interv-mapa-off-${p.clave_ruta}`}
                            title="Este parque aún no tiene coordenadas registradas">
                        <i className="fa-solid fa-location-dot"/> Sin ubicación
                      </span>
                    )}
                    <button className="btn-validar"
                            onClick={() => navigate(`/chofer/validar/${encodeURIComponent(p.clave_ruta)}`)}
                            data-testid={`interv-validar-${p.clave_ruta}`}>
                      Validar punto <i className="fa-solid fa-arrow-right"/>
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <button className="btn-ghost w-full mt-4" onClick={onUsarGPS} data-testid="btn-usar-gps">
          <i className="fa-solid fa-location-crosshairs mr-1"/> Detectar mi punto por GPS
        </button>
        <button className="btn-ghost-sm w-full mt-2" onClick={onSalir} data-testid="btn-salir-interv">
          Volver al menú
        </button>
      </div>
    </div>
  );
}
